import { type EncroachmentClientConfig } from '@/features/types';
import { capitalize } from '@/helpers/stringConversion';
import { type HoverInfo } from '@/views/MapView';
import type { PickingInfo } from 'deck.gl';

export type BucketConfig = {
  horizontalBoundaries: string[];
  verticalBoundaries: string[];
  id: number;
};

export const findBuckets = (data: EncroachmentClientConfig): BucketConfig => {
  const metric = data.config.boundaries_unit;
  const rawHorizontalBoundaries = data.config.horizontal_boundaries;
  const horizontalBoundaries = rawHorizontalBoundaries
    .slice()
    .sort((a: number, b: number) => a - b)
    .map((num: number) => (Math.abs(num) === 0.1 ? 0 : num));

  const rawVerticalBoundaries = data.config.vertical_boundaries;
  const verticalBoundaries = rawVerticalBoundaries
    .slice()
    .sort((a: number, b: number) => a - b)
    .map((num: number) => (Math.abs(num) === 0.1 ? 0 : num));

  const mappedVerticalBoundaries: string[] = [];
  const mappedHorizontalBoundaries: string[] = [];

  horizontalBoundaries.forEach((record: number, index) => {
    if (index === horizontalBoundaries.length - 1) return;
    if (index === horizontalBoundaries.length - 2)
      return mappedHorizontalBoundaries.push(`> ${record} ${metric}`);
    return mappedHorizontalBoundaries.push(
      `${record} - ${horizontalBoundaries[index + 1]}${metric}`
    );
  });

  verticalBoundaries.forEach((record: number, index) => {
    if (index === verticalBoundaries.length - 1) return;
    const absRecord = Math.abs(record);
    const nextRecord = Math.abs(verticalBoundaries[index + 1]);
    if (index === 0) {
      return mappedVerticalBoundaries.push(`> ${nextRecord}${metric} below`);
    }
    if (index === verticalBoundaries.length - 2) {
      return mappedVerticalBoundaries.push(`> ${absRecord}${metric} above`);
    }
    return mappedVerticalBoundaries.push(
      `${[absRecord, nextRecord].sort((a, b) => a - b).join(' - ')}${metric} ${
        record < 0 ? 'below' : 'above'
      }`
    );
  });

  // Short term workaround to support hitbox feature
  // We are using 0.1 as a "fake" boundary to support structures with widths
  // and setting distance vegetation that falls within this boundary to 0
  const mappedVerticalBoundariesWithZero = mappedVerticalBoundaries.map(
    (range: string) => range.replace(/0 - 0\w+.*/, 'In wire zone')
  );
  const mappedHorizontalBoundariesWithZero = mappedHorizontalBoundaries.map(
    (range: string) => range.replace(/0 - 0\w+.*/, 'In wire zone')
  );

  return {
    horizontalBoundaries: mappedHorizontalBoundariesWithZero,
    // Vertical boundary config is ascending, vertical distance buckets are descending.
    verticalBoundaries: mappedVerticalBoundariesWithZero.reverse(),
    id: data.id,
  };
};

export const handleTreeSpecies = (
  i: PickingInfo,
  setHoverInfo: (hoverInfo: HoverInfo) => void
) => {
  if (i && i?.object?.properties) {
    const { object, x, y } = i;
    const p = object.properties;
    const contents = capitalize(p.name);
    setHoverInfo({ x, y, contents });
  } else {
    setHoverInfo({});
  }
};
